import type { FeatureCollection, MultiPolygon, Point } from 'geojson'
import type {
  BeesComparisonUnit,
  CarbonComparisonUnit,
  KelpComparisonUnit,
  PlasticComparisonUnit,
  TreeComparisonUnit,
  WaterComparisonUnit,
} from '@/stores/interfaces/PublicData'

export const IMPACT_TYPES = ['trees', 'plastic', 'carbon', 'kelp', 'water', 'bees'] as const
export type ImpactType = (typeof IMPACT_TYPES)[number]
export const IMPACT_SCALE_TREES = 'trees'
export const IMPACT_SCALE_BOTTLES = 'bottles'
export const IMPACT_SCALE_KILOGRAMS = 'kilograms'
export const IMPACT_SCALE_KELP = 'kelp'
export const IMPACT_SCALE_DAYS = 'days'
export const IMPACT_SCALE_BEES = 'bees'
export const IMPACT_SCALES = [
  IMPACT_SCALE_TREES,
  IMPACT_SCALE_BOTTLES,
  IMPACT_SCALE_KILOGRAMS,
  IMPACT_SCALE_KELP,
  IMPACT_SCALE_DAYS,
  IMPACT_SCALE_BEES,
] as const
export type ImpactScale = (typeof IMPACT_SCALES)[number]

export const getPrimaryScaleForImpactType = (type: ImpactType): ImpactScale => {
  switch (type) {
    case 'trees':
      return IMPACT_SCALE_TREES
    case 'plastic':
      return IMPACT_SCALE_BOTTLES
    case 'carbon':
      return IMPACT_SCALE_KILOGRAMS
    case 'kelp':
      return IMPACT_SCALE_KELP
    case 'water':
      return IMPACT_SCALE_DAYS
    case 'bees':
      return IMPACT_SCALE_BEES
    default: {
      const exhaustiveCheck: never = type
      throw new Error(exhaustiveCheck)
    }
  }
}

export const ACCOUNT_TYPES = ['ecommerce', 'business', 'personal', 'family', 'admin'] as const
export type AccountType = (typeof ACCOUNT_TYPES)[number]

export const CURRENCY_TYPES = ['euro', 'dollar', 'pound', ''] as const
export type CurrencyType = (typeof CURRENCY_TYPES)[number]

export const PROJECT_STATUSES = ['approved', 'unapproved', 'deprecated'] as const
export type ProjectStatus = (typeof PROJECT_STATUSES)[number]

export const BILLING_PLATFORMS = ['stripe'] as const
export type BillingPlatform = (typeof BILLING_PLATFORMS)[number]

export const BACKGROUND_ICONS = ['leaves', 'circles'] as const
export type BackgroundIconType = (typeof BACKGROUND_ICONS)[number]

export enum BackgroundTheme {
  DARK = 'dark',
  LIGHT = 'light',
  CUSTOM = 'custom',
}

export const HERO_STYLES = ['center', 'side-by-side', 'align-left'] as const
export type HeroStyleType = (typeof HERO_STYLES)[number]

export const ICON_STYLES = ['simple', 'flat', 'isometric'] as const
export type IconStyleType = (typeof ICON_STYLES)[number]

export const LANGUAGES = ['en', 'de', 'fr', 'es', 'nl', 'it', 'cs'] as const
export type LanguageType = (typeof LANGUAGES)[number]
export const LOCALES = [...LANGUAGES, 'en-US'] as const
export type LocaleType = (typeof LOCALES)[number]

export const SOCIAL_MEDIA_LINKS = [
  'instagram',
  'facebook',
  'linkedin',
  'website',
  'twitter',
  'tiktok',
  'youtube',
] as const
export type SocialMediaType = (typeof SOCIAL_MEDIA_LINKS)[number]

export type TopStat = ImpactType | 'monthsEarthPositive'

export type Currency = 'dollar' | 'euro' | 'pound'

export type PlanType = 'starter' | 'growth' | 'premium'

export type ImpactColor = {
  textColor: string
  backgroundColor: string
}

export type TotalImpact = {
  [key in ImpactType]: number
}

export const PAID_SUBSCRIPTION_TYPES = [
  'ecommerce',
  'ECOMMERCE',
  'starterBusiness',
  'growthBusiness',
  'premiumBusiness',
  'enterpriseBusiness',
  'freeBusiness',
  'starterBusinessYearly',
  'growthBusinessYearly',
  'premiumBusinessYearly',
  'businessLegacy',
] as const

export const FREE_SUBSCRIPTION_TYPES = ['free', 'zero'] as const

export const SUBSCRIPTION_TYPES = [...FREE_SUBSCRIPTION_TYPES, ...PAID_SUBSCRIPTION_TYPES] as const

export enum PLAN_TYPES {
  STARTER = 'starter',
  GROWTH = 'growth',
  PREMIUM = 'premium',
}

export type SubsriptionType = (typeof SUBSCRIPTION_TYPES)[number]

export const TRANSACTION_PURCHASE_PROVIDERS = ['veritree', 'unfccc', 'plasticBank'] as const
export type TransactionPurchaseProviders = (typeof TRANSACTION_PURCHASE_PROVIDERS)[number]

export interface ISubscriptionItem {
  id: string
  product: SubsriptionType
  amount: number
  startDate: string
  trialEndDate: string
  endDate: string
  _id: string
  updatedAt: string
  createdAt: string
  payerUserId: string
}

export interface IProjectRequestParams {
  country?: string
  projectCategoryId?: string
  type?: ImpactType
  vintage?: number
  lng?: LanguageType
  statuses?: ProjectStatus[]
}

export interface IProjectPrice {
  currency: Omit<CurrencyType, ''>
  price: number
  originalPrice?: number
}

export interface IProjectUpdate {
  availability: number
  prices: IProjectPrice[]
  createdAt: Date
}

export interface IProjectPriceDescriptor {
  platform: BillingPlatform
  externalId: string
  currency: Omit<CurrencyType, ''>
}

export interface IProject {
  projectId: string
  parentProjectId?: string
  externalId?: string
  projectCategoryId: string
  name: string
  text: string
  link?: string
  externalLink?: string
  registryLink?: string
  registry?: string
  provider?: string
  imageUrl: string
  thumbnailImageUrl: string
  countries?: string[]
  default: boolean
  scale: ImpactScale
  status: ProjectStatus
  statusInitiatedAt: string
  type: ImpactType
  updates: IProjectUpdate[]
  vintage?: number
  priceDescriptors: IProjectPriceDescriptor[]
  availability: number | null
  geoJSON?: FeatureCollection<Point>
}

export interface IComponentMediaAsset {
  url: string
  previewUrl?: string
  alt?: string
  altitude?: number
  latitude?: number
  longitude?: number
}

export interface IPlantingSite {
  id: string
  latitude: number
  longitude: number
  geofences: MultiPolygon
}

export interface ISiteAssessment {
  plantingSite: string | null
  assessedAt: string | null
  assessorName: string | null
  notes?: string | null
  images?: IComponentMediaAsset[]
  videos?: IComponentMediaAsset[]
  geoJSON?: FeatureCollection[]
}

export interface IPlantingDetails {
  datePlanted: string | null
  startTime: string | null
  endTime: string | null
  siteForester: string | null
  numberOfPlanters: number | null
  notes?: string | null
  amountPlanted?: number | null
  images?: IComponentMediaAsset[]
  videos?: IComponentMediaAsset[]
  geoJSON?: FeatureCollection[]
}

export interface IPlantingSessionBlockchain {
  transactionId: string | null
  arweaveUrl: string | null
  ipfsUrl: string | null
}

export interface ISurvivabilityMetric {
  name: string
  value: number
  unit: string
  updatedAt: string
  type: string
}

export interface ISurvivabilityStats {
  fieldUpdateId: number
  metrics: ISurvivabilityMetric[]
}

export interface IExternalReport {
  externalId: string
  title: string | null
  reportedAt: string | null
  fieldUpdateId: number
  images?: IComponentMediaAsset[]
  geoJSON?: FeatureCollection[]
}

export interface IPlantingSession {
  readonly type: 'PlantingSession'
  latitude: number
  longitude: number
  altitude: number
  fieldUpdateId: number
  accountId?: string
  siteAssessment: ISiteAssessment
  planting: IPlantingDetails
  blockchain: IPlantingSessionBlockchain
  survivabilityStats?: ISurvivabilityStats | null
  externalReports?: IExternalReport[]
  site?: IPlantingSite
}

export interface IPlasticCollection {
  readonly type: 'PlasticCollection'
  accountId?: string
  collectorName: string
  buyerName: string
  materialType: string
  weight: number
  numberOfPlasticBottlesRescued: number
  geoJSON: FeatureCollection<Point>
  createdAt: string
  externalId: string
}

export interface IProjectEvidence {
  evidences: (IPlantingSession | IPlasticCollection)[]
}

export interface Impact {
  type: ImpactType
  amount: number
  projectId?: string
}

export interface IAccountImpact extends Impact {
  source: 'account'
  projectId: string
}

export interface ITransferredImpact extends Impact {
  source: 'transferred'
  urls: string[]
}

export interface IRelationImpact extends Impact {
  source: 'relation'
  projectId: string
}

export interface IImpactActionImpact extends Impact {
  source: 'impactAction'
  projectId: string
}

export interface IComparison {
  type: ImpactType
  amount: number
  suffix?: string
  description: string
  link?: string
}

export interface ICarbonComparison extends IComparison {
  type: 'carbon'
  unit: CarbonComparisonUnit
}

export interface IPlasticComparison extends IComparison {
  type: 'plastic'
  unit: PlasticComparisonUnit
}

export interface ITreeComparison extends IComparison {
  type: 'trees'
  unit: TreeComparisonUnit
}

export interface IKelpComparison extends IComparison {
  type: 'kelp'
  unit: KelpComparisonUnit
}

export interface IWaterComparison extends IComparison {
  type: 'water'
  unit: WaterComparisonUnit
}

export interface IBeesComparison extends IComparison {
  type: 'bees'
  unit: BeesComparisonUnit
}

export const PUBLIC_ACCOUNT_TYPES = ['business', 'personal', 'family']
export type PublicAccountType = (typeof PUBLIC_ACCOUNT_TYPES)[number]

export interface IComparisons {
  comparisons: (ICarbonComparison | IPlasticComparison | ITreeComparison | IKelpComparison)[]
}

export interface IPublicAccount extends IComparisons {
  name: string
  signupDate: string
  accountType: PublicAccountType
  impacts: (IAccountImpact | IRelationImpact | ITransferredImpact | IImpactActionImpact)[]
}

export interface ICommunityAccount extends IComparisons {
  impacts: (IAccountImpact | IRelationImpact)[]
}

export interface IReceiptItem {
  id?: string
  name: string
  url: string
}

export interface IReceipt {
  date: string
  time: number
  type: ImpactType
  receipts: IReceiptItem[]
}

export interface TransactionPurchase {
  id: string
  type: ImpactType
  amount: number
  projectId: string
  receiptUrl: string
  provider: TransactionPurchaseProviders
  periodStartDate: string
  periodEndDate: string
}

export interface IHeroContent {
  title: string
  subtitle: string
}

export interface IHeroSettings {
  style: HeroStyleType
  logo: {
    url: string
    visible: boolean
  }
  image: {
    url: string
    visible: boolean
    overlayColor: string
    textColor: string
  }
  content: Record<LanguageType, IHeroContent>
  links: Record<SocialMediaType, string>
  memberSince: {
    visible: boolean
  }
}

export interface IGoalSettings {
  goals: string[]
  achievements: string[]
}

export interface IMenuItem {
  lang: LanguageType
  label: string
  link: string
  target?: '_blank' | '_self'
  color: string
  backgroundColor: string
  type: 'button' | 'link'
}

export type ThemeColor = {
  background: string
  text: string
}

export interface IEditorSettings {
  whiteLabel?: {
    domain: string
    navbar: {
      menuItems: IMenuItem[]
      buttons: IMenuItem[]
    }
    footer: {
      menuItems: IMenuItem[]
    }
  }
  joinGreenspark?: {
    visible: boolean
  }
  background: {
    theme: BackgroundTheme
    icon: BackgroundIconType
    themeColor: ThemeColor
  }
  hero: IHeroSettings
  icon: {
    style: IconStyleType
    impactColors: Record<ImpactType, ImpactColor>
    impactDisplay: Record<TopStat | 'hideZeroValues', boolean>
  }
  goalsAchievements: Record<LanguageType, IGoalSettings>
}

export interface IPublicPageEditor {
  publicPageEditorSettings: IEditorSettings
  publicPageEditorOptions: {
    heroStyles: HeroStyleType
  }
}

export interface IAccount {
  accountId: string
  accountType: AccountType | ''
  companyName: string
  website: string
  vatId: string
  profileDescription: string
  imageURL?: string
  signupId: string
  signupComplete: boolean
  emailAlreadyUsed: boolean
  birthday: {
    year: number | null
    month: number | null
    day: number | null
  }
  phoneNumber: string
  marketingAccepted: boolean
  termsAndConditions: boolean
  address: {
    street: string
    postCode: string
    city: string
    country: string
  }
  personalPlans: {
    personal: null
    plasticHero: null
    earthHero: number | null
    positiveFamilyAdult: null
    positiveFamilyChild: null
    positiveFamilyPet: null
    earthPositiveFamilyAdult: null
    earthPositiveFamilyChild: null
    earthPositiveFamilyPet: null
  }
  widgetFolderName: string
  widgets: {
    type: string
    url: string
  }[]
  livePageId: string
  signupDate: string
  currency: Currency
  currencyPreChange: Currency
  relationImpact?: Record<ImpactType, number>
  impactActionImpact?: Record<ImpactType, number>
  userFromMarketplace: boolean
  discounts: {
    type: string
    amount: number | null
  }[]
  sustainabilitySite: string
  createdAt: string
}
